/* The Content Generator, Layout B: stacked steps.

   A layout only. Every control is an element built by GeneratorPanel and
   handed in through `slots`; this file decides where each one sits and which
   step is open. It owns no form state — a topic typed in Step 1 is the same
   `blogTopic` v1 reads — so switching layouts loses nothing, and the
   case-study body is byte-for-byte the v1 one because it IS the v1 element.

   Steps unlock in order but never trap: Generate is reachable as soon as
   there is a topic (decision 2 — gaps are information, not a gate), and a
   step that has sent questions rests at "waiting on the client" while the
   rest of the generator stays usable.

   Reached with ?gen=v2 (remembered per browser) until Phase 6 makes it the
   default. ?gen=v1 is the escape hatch.
*/
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { getOpenGapSets, getPageDetail } from '@/data/client';
import { TopicInbox, type InboxHandlers, type ManagerKey } from './TopicInbox';

export type GenTab = 'setup' | 'case_study' | 'blog' | 'prompt' | 'rewrite' | 'web_handoff' | 'site_page' | 'image';

const STORAGE_KEY = 'tiq.generator';

/* ?gen=v2 switches and remembers; ?gen=v1 switches back and forgets. The
   param is consumed so a shared link does not pin a colleague to a layout. */
export function useGeneratorVersion(): 'v1' | 'v2' {
  const [params, setParams] = useSearchParams();
  const asked = params.get('gen');
  const [version, setVersion] = useState<'v1' | 'v2'>(() => {
    if (asked === 'v2' || asked === 'v1') return asked;
    try { return localStorage.getItem(STORAGE_KEY) === 'v2' ? 'v2' : 'v1'; } catch { return 'v1'; }
  });
  useEffect(() => {
    if (asked !== 'v2' && asked !== 'v1') return;
    setVersion(asked);
    try { if (asked === 'v2') localStorage.setItem(STORAGE_KEY, 'v2'); else localStorage.removeItem(STORAGE_KEY); } catch { /* private window */ }
    setParams((p) => { const n = new URLSearchParams(p); n.delete('gen'); return n; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asked]);
  return version;
}

export interface GeneratorSlots {
  blogTopicRow: ReactNode;
  blogGroundSelect: ReactNode;
  /** "grounded in N interviews" only — the support badge is pinned above the steps. */
  blogGroundedBadge: ReactNode;
  /** showSend=false hides the card's own Send for approval so the layout can place one below the outline. */
  blogCoverageFor: (opts: { showSend?: boolean }) => ReactNode;
  blogOutline: ReactNode;
  blogNotesEl: ReactNode;
  /** route set = the page-FAQ sender (answers land on that page); unset = the blog gap sender. */
  gapsFor: (opts: { route?: string; onSent?: (count: number) => void }) => ReactNode;
  blogManagers: ReactNode;
  /** The topic idea briefs card (authoring + triage), shown with the managers in v2. */
  briefsPanel?: ReactNode;
  promptBody: ReactNode;
  rewriteBody: ReactNode;
  sitePageSelects: ReactNode;
  controlsFor: (opts?: { sitePagePicker?: boolean }) => ReactNode;
  hintsEl: ReactNode;
  handoffHint: ReactNode;
  sitePageBody: ReactNode;
  templateEl: ReactNode;
  messagesEl: ReactNode;
  outputEl: ReactNode;
  setupTab: ReactNode;
  imageStudio: ReactNode;
}

export interface GeneratorV2Props {
  tab: GenTab;
  onTab: (t: GenTab) => void;
  slots: GeneratorSlots;
  headRef: RefObject<HTMLDivElement>;
  flash: boolean;
  blogTopic: string;
  hasCoverage: boolean;
  checking: boolean;
  outlineCount: number;
  gapCount: number;
  busy: boolean;
  sitePage: string;
  /** The page's route (/services/x/) once a site page is picked — Step 3's sender needs it. */
  sitePageRoute: string;
  onCheckSitePage: () => void;
  support: { url: string; anchor: string; keywords: string } | null;
  onClearSupport: () => void;
  onSendForApproval: () => void;
  sendState: { sending: boolean; sent: boolean; label?: ReactNode };
  inbox: Omit<InboxHandlers, 'onOpenManager'>;
}

const PRIMARY: { key: GenTab; label: string }[] = [
  { key: 'blog', label: 'Blog' },
  { key: 'case_study', label: 'Case study' },
  { key: 'site_page', label: 'Site page' },
];
const MORE: { key: GenTab; label: string }[] = [
  { key: 'prompt', label: 'Prompt' },
  { key: 'rewrite', label: 'Rewrite' },
  { key: 'web_handoff', label: 'Web handoff' },
  { key: 'image', label: 'AI image' },
];

const daysAgo = (iso: string) => Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 86_400_000));
const fmtDay = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

export function GeneratorV2(p: GeneratorV2Props) {
  const { slots, tab } = p;
  const [more, setMore] = useState(false);
  const stepped = tab === 'blog' || tab === 'site_page';
  const site = tab === 'site_page';

  /* Which step is open and the furthest one reached. Reset when the subject
     changes — a new topic is a new path — but not when a step merely closes. */
  const subject = site ? p.sitePage : p.blogTopic;
  const [step, setStep] = useState(0);
  const [max, setMax] = useState(0);
  const [lastSubject, setLastSubject] = useState(subject);
  if (subject !== lastSubject) { setLastSubject(subject); setStep(0); setMax(0); }
  const go = (n: number) => { setStep(n); setMax((m) => Math.max(m, n)); };
  // A fresh coverage result opens Step 2 — the thing the person pressed the button for.
  const [sawCoverage, setSawCoverage] = useState(false);
  useEffect(() => {
    if (p.hasCoverage && !sawCoverage) { setSawCoverage(true); go(1); }
    if (!p.hasCoverage && sawCoverage) setSawCoverage(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.hasCoverage]);

  /* Resting state for Step 3, read from where the questions actually went:
     a blog's gap set (matched by title), or the page worksheet's asked list.
     Session sends update it at once; the queries catch up on reload. */
  const [path, setPath] = useState<'have' | 'find'>('have');
  const mgrRef = useRef<HTMLDetailsElement>(null);
  const [mgrOpen, setMgrOpen] = useState(false);
  /* The inbox rows link to "their" manager. The managers are the existing
     accordions, each with its own toggle, so the link opens the group and
     scrolls to it; the person opens the one they want. `key` is kept for
     when the accordions accept an open prop. */
  const openManager = (_key: ManagerKey) => {
    setMgrOpen(true);
    setTimeout(() => mgrRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  };
  const inbox: InboxHandlers = {
    onPick: (t) => { p.inbox.onPick(t); setPath('have'); },
    onUseQueued: (r) => { p.inbox.onUseQueued(r); setPath('have'); },
    onGenerateNow: (r) => { p.inbox.onGenerateNow(r); setPath('have'); go(3); },
    onUseVault: (a) => { p.inbox.onUseVault(a); setPath('have'); },
    onUsePage: (c, pg) => { p.inbox.onUsePage(c, pg); setPath('have'); },
    onUseMined: (c, ids) => { p.inbox.onUseMined(c, ids); setPath('have'); },
    onOpenManager: openManager,
  };
  // Writing started elsewhere (Generate now) — show the step that has the output controls.
  useEffect(() => { if (p.busy && stepped) go(3); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.busy]);
  const [sentNow, setSentNow] = useState<{ count: number; at: string } | null>(null);
  useEffect(() => { setSentNow(null); }, [subject]);
  const { data: openSets } = useQuery({ queryKey: ['openGapSets'], queryFn: getOpenGapSets, enabled: !site && !!p.blogTopic.trim(), staleTime: 60_000 });
  const { data: pageDetail } = useQuery({ queryKey: ['pageDetail', p.sitePageRoute], queryFn: () => getPageDetail(p.sitePageRoute), enabled: site && !!p.sitePageRoute, staleTime: 60_000 });
  const blogSet = !site ? openSets?.items.find((s) => s.title.trim().toLowerCase() === p.blogTopic.trim().toLowerCase()) : undefined;
  const pageAsked = site ? pageDetail?.asked?.slice().sort((a, b) => Date.parse(b.sentAt) - Date.parse(a.sentAt))[0] : undefined;
  const rest = sentNow
    ? { count: sentNow.count, at: sentNow.at, answered: 0, total: sentNow.count }
    : blogSet ? { count: blogSet.total, at: blogSet.sentAt, answered: blogSet.answered + blogSet.declined, total: blogSet.total }
      : pageAsked ? { count: pageAsked.total, at: pageAsked.sentAt, answered: pageAsked.answered + pageAsked.declined, total: pageAsked.total }
        : null;
  const answersIn = !!rest && rest.total > 0 && rest.answered >= rest.total;

  const unlocked = (i: number) =>
    i === 0 ? true
      : i === 1 ? p.hasCoverage
        : i === 2 ? p.hasCoverage
          : !!subject.trim(); // Generate: a topic is enough (decision 2)

  const steps = [
    {
      t: site ? 'Page' : 'Topic',
      sum: subject.trim() ? subject : (site ? 'Pick the page' : 'Type one or find one'),
      body: site ? (
        <div className="gen2-step__body">
          <div className="gen2-row">{slots.sitePageSelects}</div>
          <div className="bq-actions">
            <Button variant="primary" size="sm" disabled={p.checking || !p.sitePage} iconLeft={<Icon name="check-circle" size={14} />} onClick={p.onCheckSitePage}>
              {p.checking ? 'Checking…' : 'Check the Vault'}
            </Button>
            <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>What the vault already knows about this page’s subject — and what nobody has said yet.</span>
          </div>
        </div>
      ) : (
        <div className="gen2-step__body">
          <div className="gen2-seg" role="tablist" aria-label="How to choose a topic">
            <button type="button" role="tab" aria-selected={path === 'have'} className={path === 'have' ? 'is-on' : ''} onClick={() => setPath('have')}><Icon name="file-text" size={14} /> I have a topic</button>
            <button type="button" role="tab" aria-selected={path === 'find'} className={path === 'find' ? 'is-on' : ''} onClick={() => setPath('find')}><Icon name="search" size={14} /> Browse topic inbox</button>
          </div>
          {path === 'have' ? (
            <>
              {slots.blogTopicRow}
              {slots.blogGroundedBadge}
            </>
          ) : <TopicInbox h={inbox} />}
        </div>
      ),
    },
    {
      t: 'Coverage',
      sum: p.hasCoverage ? `${p.outlineCount ? `${p.outlineCount} in the outline` : 'Checked'}` : 'What the vault supports',
      body: (
        <div className="gen2-step__body">
          {slots.blogCoverageFor({ showSend: false })}
          {!site && (
            <>
              <div className="gen2-h3">Article outline <span className="dash-muted">(the sub-topic headings the writer generates against)</span></div>
              {slots.blogOutline}
              {/* Below the outline, and sending the outline (decision 1): the
                  brief the Optimization Manager approves is the brief written. */}
              <div className="bq-actions">
                <Button size="sm" variant="primary" disabled={p.sendState.sending || !p.blogTopic.trim()} iconLeft={<Icon name="send" size={13} />} onClick={p.onSendForApproval}>
                  {p.sendState.sending ? 'Sending…' : 'Send for approval'}
                </Button>
                {p.sendState.sent
                  ? <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{p.sendState.label}</span>
                  : <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>Topic + outline go to the Optimization Manager, theirs to edit or approve.</span>}
              </div>
            </>
          )}
          <div className="gen2-acts">
            <Button size="sm" variant="primary" onClick={() => go(2)}>Continue to gaps <Icon name="arrow-right" size={13} /></Button>
            <Button size="sm" variant="ghost" onClick={() => go(3)}>Skip to generate</Button>
          </div>
        </div>
      ),
    },
    {
      t: 'Gaps',
      sum: rest
        ? (answersIn ? `${rest.answered} of ${rest.total} answered · vault updated` : `${rest.count} question${rest.count === 1 ? '' : 's'} sent · waiting on the client since ${fmtDay(rest.at)}`)
        : p.gapCount ? `${p.gapCount} to ask` : 'Ask the client to fill vault gaps',
      body: (
        <div className="gen2-step__body">
          {rest && !answersIn && (
            <p className="gen2-rest"><Icon name="clock" size={13} /> <strong>{rest.count} question{rest.count === 1 ? '' : 's'} sent</strong> · waiting on the client since {fmtDay(rest.at)} ({daysAgo(rest.at)}d). You can still generate now with what’s in the vault.</p>
          )}
          {rest && answersIn && (
            <p className="gen2-rest gen2-rest--in"><Icon name="check" size={13} /> Answers are in — re-check the Vault in Step 1 to write with them.</p>
          )}
          {p.gapCount
            ? <p className="dash-muted" style={{ margin: 0, fontSize: 'var(--text-xs)' }}>Only these are asked. {site ? 'Answers land as FAQs on this page.' : 'Your outline goes along as background so the client can see what the article is for.'} Answers return to the Vault, usually days later.</p>
            : <p className="dash-muted" style={{ margin: 0 }}>No gaps from the last check — the vault covers this, or nothing has been checked yet.</p>}
          {slots.gapsFor({ route: site ? p.sitePageRoute : undefined, onSent: (count) => { setSentNow({ count, at: new Date().toISOString() }); go(3); } })}
          <div className="gen2-acts">
            <Button size="sm" variant="ghost" onClick={() => go(3)}>Skip — write it anyway <Icon name="arrow-right" size={13} /></Button>
          </div>
        </div>
      ),
    },
    {
      t: 'Generate',
      sum: 'Output & writer settings',
      body: (
        <div className="gen2-step__body gen2-step__body--flush">
          {!site && slots.blogNotesEl}
          {!site && <div className="gen2-row">{slots.blogGroundSelect}</div>}
          {site && slots.sitePageBody}
          {slots.controlsFor({ sitePagePicker: false })}
          {slots.hintsEl}
          {!site && (
            <details className="gen2-adv">
              <summary><Icon name="chevron-right" size={13} /> Advanced</summary>
              {slots.templateEl}
            </details>
          )}
          {slots.messagesEl}
        </div>
      ),
    },
  ];

  const tabBtn = (t: { key: GenTab; label: string }) => (
    <button key={t.key} type="button" className={`vault-tab ${tab === t.key ? 'is-on' : ''}`} onClick={() => { p.onTab(t.key); setMore(false); }}>{t.label}</button>
  );
  const moreOn = MORE.some((m) => m.key === tab);

  return (
    <Card padding={false} className={`dash-leads vault gen gen2${p.flash ? ' gen-flash' : ''}`}>
      <div className="dash-panelhead dash-panelhead--pad" ref={p.headRef}>
        <div>
          <h2><Icon name="sparkles" size={17} /> Content generator</h2>
          <p>Write grounded content from this client&rsquo;s vault. Admin&nbsp;only.</p>
        </div>
      </div>

      <div className="vault-add__methods gen2-tabs" style={{ padding: '12px 20px 0' }}>
        {PRIMARY.map(tabBtn)}
        <div className="gen2-more">
          <button type="button" className={`vault-tab ${moreOn ? 'is-on' : ''}`} onClick={() => setMore((v) => !v)} aria-haspopup="menu" aria-expanded={more}>
            {moreOn ? MORE.find((m) => m.key === tab)!.label : 'More'} <Icon name={more ? 'chevron-down' : 'chevron-right'} size={12} />
          </button>
          {more && (
            <div className="gen2-menu" role="menu">
              {MORE.map((m) => (
                <button key={m.key} type="button" role="menuitem" className={tab === m.key ? 'is-on' : ''} onClick={() => { p.onTab(m.key); setMore(false); }}>{m.label}</button>
              ))}
            </div>
          )}
        </div>
        <button type="button" className={`vault-tab gen2-gear ${tab === 'setup' ? 'is-on' : ''}`} onClick={() => { p.onTab('setup'); setMore(false); }} title="Setup" aria-label="Setup">
          <Icon name="settings" size={14} />
        </button>
      </div>

      {tab === 'setup' && slots.setupTab}
      {tab === 'image' && slots.imageStudio}

      {stepped && (
        <div className="gen2-flow">
          {p.support && (
            <div className="gen2-support">
              <Icon name="link" size={14} />
              <span>Supports <code>{p.support.anchor || p.support.url}</code>{p.support.keywords ? <> · <em>{p.support.keywords}</em></> : null}. The post is written to strengthen this page.</span>
              <button type="button" className="bq-add" onClick={p.onClearSupport}>Clear</button>
            </div>
          )}
          <div className="gen2-steps">
            {steps.map((s, i) => {
              const cur = i === step;
              const open = unlocked(i);
              const done = i < step || (i <= max && !cur);
              const waiting = i === 2 && !!rest;
              return (
                <section key={s.t} className={`gen2-step${cur ? ' gen2-step--cur' : ''}${open ? '' : ' gen2-step--lock'}`} aria-current={cur ? 'step' : undefined}>
                  <header className="gen2-step__head">
                    <span className={`gen2-num${cur ? ' gen2-num--on' : done ? ' gen2-num--done' : ''}`}>{done && !cur && !waiting ? <Icon name="check" size={14} /> : i + 1}</span>
                    <strong>{s.t}</strong>
                    {waiting && <Badge variant={answersIn ? 'success' : 'warning'} dot>{answersIn ? 'Answers in' : 'Waiting on client'}</Badge>}
                    <em>{cur ? '' : s.sum}</em>
                    {!cur && open && <Button size="sm" variant="outline" onClick={() => setStep(i)}>{i < step ? 'Edit' : 'Open'}</Button>}
                  </header>
                  {cur && s.body}
                </section>
              );
            })}
          </div>
          {!site && (
            <details className="gen2-managers" ref={mgrRef} open={mgrOpen} onToggle={(e) => setMgrOpen((e.target as HTMLDetailsElement).open)}>
              <summary><Icon name="chevron-right" size={13} /> Topic managers <span className="dash-muted">— briefs, the queue, sitemap support, covered topics</span></summary>
              <div className="gen2-managers__body">
                {slots.briefsPanel}
                {slots.blogManagers}
              </div>
            </details>
          )}
        </div>
      )}

      {/* Case study is one step, so it gets one step's frame: the same pane,
          grid and Advanced fold as Blog's Step 4, around the v1 controls
          unchanged (decision 4 — the element is the one v1 renders). */}
      {tab === 'case_study' && (
        <div className="gen2-flow">
          <section className="gen2-step gen2-step--cur" aria-current="step">
            <header className="gen2-step__head">
              <span className="gen2-num gen2-num--on"><Icon name="file-text" size={14} /></span>
              <strong>Case study</strong>
              <em>From an interview with a transcript — pick a story, or leave it on auto.</em>
            </header>
            <div className="gen2-step__body gen2-step__body--flush">
              {slots.controlsFor()}
              {slots.hintsEl}
              <details className="gen2-adv">
                <summary><Icon name="chevron-right" size={13} /> Advanced</summary>
                {slots.templateEl}
              </details>
              {slots.messagesEl}
            </div>
          </section>
        </div>
      )}

      {(tab === 'prompt' || tab === 'rewrite' || tab === 'web_handoff') && (
        <>
          {slots.promptBody}
          {slots.rewriteBody}
          {slots.controlsFor()}
          {slots.handoffHint}
          {slots.messagesEl}
        </>
      )}

      {slots.outputEl}
    </Card>
  );
}
